import React, { useState } from 'react';
import { X, CheckCircle, Calendar, AlertTriangle } from 'lucide-react';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import Input from '../../../components/ui/Input';
import dealService from '../../../services/dealService';

const BulkActionsModal = ({ selectedDeals, onClose, onSuccess }) => {
  const [action, setAction] = useState('');
  const [newStatus, setNewStatus] = useState('active');
  const [extensionDays, setExtensionDays] = useState('7');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError('');

    if (!action) {
      setError('Please select an action');
      return;
    }

    try {
      setLoading(true);

      switch (action) {
        case 'update_status':
          await dealService?.bulkUpdateStatus(selectedDeals, newStatus);
          break;
        case 'extend_expiration':
          await dealService?.bulkExtendExpiration(selectedDeals, parseInt(extensionDays));
          break;
        default:
          throw new Error('Invalid action');
      }

      onSuccess();
    } catch (err) {
      console.error('Error performing bulk action:', err);
      setError(err?.message || 'Failed to perform bulk action');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-bold text-gray-900">Bulk Actions</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-red-600 text-sm">{error}</p>
            </div>
          )}

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-900">
              <span className="font-semibold">{selectedDeals?.length}</span> deal(s) selected
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Action *
            </label>
            <Select
              value={action}
              onChange={(e) => setAction(e?.target?.value)}
              required
            >
              <option value="">Choose an action...</option>
              <option value="update_status">Update Status</option>
              <option value="extend_expiration">Extend Expiration</option>
            </Select>
          </div>

          {action === 'update_status' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                New Status *
              </label>
              <Select
                value={newStatus}
                onChange={(e) => setNewStatus(e?.target?.value)}
                required
              >
                <option value="active">Active</option>
                <option value="scheduled">Scheduled</option>
                <option value="paused">Paused</option>
                <option value="expired">Expired</option>
              </Select>
              <div className="mt-3 flex items-start gap-2 text-sm text-gray-600">
                <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <p>All selected deals will be updated to {newStatus} status</p>
              </div>
            </div>
          )}

          {action === 'extend_expiration' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Extension Days *
              </label>
              <Input
                type="number"
                min="1"
                max="365"
                value={extensionDays}
                onChange={(e) => setExtensionDays(e?.target?.value)}
                placeholder="7"
                required
              />
              <div className="mt-3 flex items-start gap-2 text-sm text-gray-600">
                <Calendar className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <p>Expiration dates will be extended by {extensionDays} day(s)</p>
              </div>
            </div>
          )}

          {action && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-yellow-900">Warning</p>
                  <p className="text-sm text-yellow-800 mt-1">
                    This action will affect {selectedDeals?.length} deal(s) and cannot be undone.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
            <Button
              type="button"
              onClick={onClose}
              variant="outline"
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              loading={loading}
              disabled={!action}
            >
              Apply Action
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BulkActionsModal;